/**
 * UserMultiSelector - 多用戶選擇器
 * 
 * 功能：
 * 1. 搜尋用戶
 * 2. 選擇多個用戶
 * 3. 顯示已選用戶
 * 4. 支持串行/並行模式選擇
 */

import React, { useState, useCallback } from 'react';
import { useUserSearch } from '../../../hooks/useApi';
import { useI18n } from '../../context/I18nContext';

// 用戶搜尋結果項
function UserSearchItem({ user, isSelected, onToggle }) {
    return (
        <button
            onClick={() => onToggle(user)}
            className={`w-full flex items-center gap-3 p-3 text-left border-b last:border-b-0 transition-colors ${
                isSelected 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'hover:bg-gray-50'
            }`}
        >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                isSelected ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
                {user.displayName?.[0] || '?'}
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                    {user.displayName}
                </div>
                <div className="text-xs text-gray-500 truncate">
                    {user.department} • {user.jobTitle}
                </div>
            </div>
            {isSelected && (
                <svg className="w-5 h-5 text-blue-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
            )}
        </button>
    );
}

// 已選用戶標籤
function SelectedUserTag({ user, onRemove, index, showOrder }) {
    return (
        <div className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
            {showOrder && (
                <span className="w-5 h-5 flex items-center justify-center bg-blue-200 rounded-full text-xs font-medium">
                    {index + 1}
                </span>
            )}
            <span className="truncate max-w-[120px]">{user.displayName}</span>
            <button
                onClick={() => onRemove(user)}
                className="ml-1 text-blue-600 hover:text-blue-800"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
}

/**
 * UserMultiSelector 主組件
 * 
 * @param {Object} props
 * @param {Array} props.selectedUsers - 已選擇的用戶列表
 * @param {Function} props.onChange - 選擇變更回調 (users) => void
 * @param {string} props.approvalMode - 審批模式: 'sequential' | 'parallel_any' | 'parallel_all'
 * @param {Function} props.onApprovalModeChange - 審批模式變更回調
 * @param {number} props.maxUsers - 最大選擇人數
 * @param {number} props.minUsers - 最小選擇人數
 * @param {boolean} props.showApprovalMode - 是否顯示審批模式選擇
 * @param {Function} props.onConfirm - 確認選擇回調
 * @param {Function} props.onCancel - 取消回調
 * @param {string} props.title - 標題
 */
export default function UserMultiSelector({
    selectedUsers = [],
    onChange,
    approvalMode = 'sequential',
    onApprovalModeChange,
    maxUsers = 5,
    minUsers = 1,
    showApprovalMode = true,
    onConfirm,
    onCancel,
    title,
}) {
    const { t } = useI18n(['common', 'forms']);
    const [query, setQuery] = useState('');
    const [internalSelected, setInternalSelected] = useState([]);
    const [internalMode, setInternalMode] = useState(approvalMode);

    // 同步外部 selectedUsers 到內部狀態
    // 當 Modal 開啟時（selectedUsers 改變），確保內部狀態是最新的
    React.useEffect(() => {
        // 深拷貝以避免引用問題
        setInternalSelected(selectedUsers.map(u => ({ ...u })));
    }, [selectedUsers]);

    // 同步外部 approvalMode 到內部狀態
    React.useEffect(() => {
        setInternalMode(approvalMode);
    }, [approvalMode]);

    const { data: searchResult, isLoading } = useUserSearch(
        query.length >= 2 ? query : null
    );
    const users = searchResult?.data?.users || searchResult?.data || [];

    // 檢查用戶是否已選
    const isUserSelected = useCallback((user) => {
        return internalSelected.some(u => 
            u.userId === user.id || 
            u.userId === user.userId || 
            u.email === user.email
        );
    }, [internalSelected]);

    // 切換用戶選擇
    const handleToggleUser = useCallback((user) => {
        const normalizedUser = {
            userId: user.id || user.userId,
            displayName: user.displayName,
            email: user.mail || user.email,
            department: user.department,
            jobTitle: user.jobTitle,
        };

        setInternalSelected(prev => {
            const exists = prev.some(u => u.userId === normalizedUser.userId || u.email === normalizedUser.email);
            if (exists) {
                return prev.filter(u => u.userId !== normalizedUser.userId && u.email !== normalizedUser.email);
            } else {
                if (prev.length >= maxUsers) {
                    return prev;
                }
                return [...prev, normalizedUser];
            }
        });
    }, [maxUsers]);

    // 移除已選用戶
    const handleRemoveUser = useCallback((user) => {
        setInternalSelected(prev => 
            prev.filter(u => u.userId !== user.userId && u.email !== user.email)
        );
    }, []);

    // 確認選擇
    const handleConfirm = () => {
        if (internalSelected.length < minUsers) {
            return;
        }
        if (onChange) {
            onChange(internalSelected);
        }
        if (onApprovalModeChange) {
            onApprovalModeChange(internalMode);
        }
        if (onConfirm) {
            onConfirm({
                users: internalSelected,
                approvalMode: internalMode
            });
        }
    };

    // 審批模式選項
    const approvalModes = [
        { 
            value: 'sequential', 
            label: t('forms:route.modeSequential') || '依序審批',
            description: t('forms:route.modeSequentialDesc') || '按順序一個接一個審批'
        },
        { 
            value: 'parallel_any', 
            label: t('forms:route.modeParallelAny') || '任一審批',
            description: t('forms:route.modeParallelAnyDesc') || '任一人審批通過即可'
        },
        { 
            value: 'parallel_all', 
            label: t('forms:route.modeParallelAll') || '全部審批',
            description: t('forms:route.modeParallelAllDesc') || '所有人都需審批通過'
        },
    ];

    return (
        <div className="w-full max-w-md mx-auto">
            {/* 標題 */}
            {title && (
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {title}
                </h3>
            )}

            {/* 已選用戶 */}
            {internalSelected.length > 0 && (
                <div className="mb-4">
                    <div className="text-sm text-gray-500 mb-2">
                        {t('forms:route.selectedApprovers') || '已選審批人'} ({internalSelected.length}/{maxUsers})
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {internalSelected.map((user, idx) => (
                            <SelectedUserTag
                                key={user.userId || user.email}
                                user={user}
                                index={idx}
                                onRemove={handleRemoveUser}
                                showOrder={internalMode === 'sequential'}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* 審批模式選擇 */}
            {showApprovalMode && internalSelected.length > 1 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                        {t('forms:route.approvalMode') || '審批模式'}
                    </div>
                    <div className="space-y-2">
                        {approvalModes.map(mode => (
                            <label
                                key={mode.value}
                                className={`flex items-start gap-3 p-2 rounded cursor-pointer ${
                                    internalMode === mode.value ? 'bg-blue-50' : 'hover:bg-white'
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="approvalMode"
                                    value={mode.value}
                                    checked={internalMode === mode.value}
                                    onChange={(e) => setInternalMode(e.target.value)}
                                    className="mt-1"
                                />
                                <div>
                                    <div className="font-medium text-sm">{mode.label}</div>
                                    <div className="text-xs text-gray-500">{mode.description}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* 搜尋輸入 */}
            <div className="mb-3">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('forms:route.searchPlaceholder') || '輸入姓名或 Email 搜尋...'}
                    className="w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                />
            </div>

            {/* 搜尋結果 */}
            <div className="border rounded-lg bg-white max-h-64 overflow-y-auto">
                {query.length < 2 && (
                    <div className="p-4 text-center text-gray-500 text-sm">
                        {t('forms:route.searchHint') || '請輸入至少 2 個字元開始搜尋'}
                    </div>
                )}
                {query.length >= 2 && isLoading && (
                    <div className="p-4 text-center text-gray-500 text-sm">
                        <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                        {t('forms:route.searching') || '搜尋中...'}
                    </div>
                )}
                {query.length >= 2 && !isLoading && users.length > 0 && (
                    users.map(user => (
                        <UserSearchItem
                            key={user.id || user.userId || user.mail}
                            user={user}
                            isSelected={isUserSelected(user)}
                            onToggle={handleToggleUser}
                        />
                    ))
                )}
                {query.length >= 2 && !isLoading && users.length === 0 && (
                    <div className="p-4 text-center text-gray-500 text-sm">
                        {t('forms:route.noResults') || '找不到符合的用戶'}
                    </div>
                )}
            </div>

            {/* 操作按鈕 */}
            <div className="mt-4 flex justify-end gap-3">
                {onCancel && (
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                        {t('common:cancel') || '取消'}
                    </button>
                )}
                <button
                    onClick={handleConfirm}
                    disabled={internalSelected.length < minUsers}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {t('common:confirm') || '確認'} {internalSelected.length > 0 && `(${internalSelected.length})`}
                </button>
            </div>
        </div>
    );
}
